import { AbsoluteModelPath, AbsoluteResolvedModelPath } from "../../../../../Common/ModelPath/ModelPath";
import { IErrorManager } from "../ErrorManager/ErrorManager";
import { IModel } from "../ModelStoring/Model";
import { MaskResolver } from "../PathUtils/MaskResolver";
import { ModelEvent } from "../EventManagement/ModelEvents/ModelEvent";
import { InvalidArgumentError } from "../../../../../Common/Errors";
import { IPicklistsProvider } from "../Picklists/IPicklistsProvider";
import { IExternalPropertiesProvider } from "../../Form/IExternalPropertiesProvider";

import { IEvaluationContext } from "./IEvaluationContext";
import { IterationContext } from "./ModelPath/IterationContext";
import { IIgnoredPathChecker } from "./IIgnoredPathChecker";

export class EvaluationContext implements IEvaluationContext {
    public readonly model: IModel;
    public readonly relatedEvents: ModelEvent[];
    private readonly ignoredPathChecker: IIgnoredPathChecker;
    private readonly iterationContexts: readonly IterationContext[] = [];
    private readonly errorManager: IErrorManager;
    private readonly externalPropertiesProvider: IExternalPropertiesProvider;
    private readonly picklistsProvider: IPicklistsProvider;

    public constructor(
        model: IModel,
        errorManager: IErrorManager,
        relatedEvents: Iterable<ModelEvent>,
        ignoredPathChecker: IIgnoredPathChecker,
        picklistsProvider: IPicklistsProvider,
        externalPropertiesProvider: IExternalPropertiesProvider,
        iterationContexts: readonly IterationContext[] = []
    ) {
        this.externalPropertiesProvider = externalPropertiesProvider;
        this.picklistsProvider = picklistsProvider;
        this.ignoredPathChecker = ignoredPathChecker;
        this.model = model;
        this.iterationContexts = iterationContexts;
        this.errorManager = errorManager;
        this.relatedEvents = [...relatedEvents];
    }

    public getPicklistProvider(): IPicklistsProvider {
        return this.picklistsProvider;
    }

    public getExternalPropertiesProvider(): IExternalPropertiesProvider {
        return this.externalPropertiesProvider;
    }

    public set(pathParts: AbsoluteModelPath, value: string | undefined): void {
        return this.model.setValue(this.resolveIterationContextsStrict(pathParts), value);
    }

    public resolvePath(targetPath: AbsoluteModelPath): AbsoluteModelPath {
        return this.resolveIterationContexts(targetPath);
    }

    public setError(targetPath: AbsoluteModelPath, error: string | undefined): void {
        const targetLegacyPath = this.resolveIterationContextsStrict(targetPath);
        if (error === undefined) {
            this.errorManager.clearCached(this.model, targetLegacyPath);
        } else {
            this.errorManager.clearCached(this.model, targetLegacyPath);
            this.errorManager.addErrorCached(this.model, targetLegacyPath, error);
        }
    }

    public setWarning(targetPath: AbsoluteModelPath, error: string | undefined): void {
        const targetLegacyPath = this.resolveIterationContexts(targetPath).toLegacyPath();
        if (error === undefined) {
            this.errorManager.clearCached(this.model, targetLegacyPath);
        } else {
            this.errorManager.clearCached(this.model, targetLegacyPath);
            this.errorManager.addWarningCached(this.model, targetLegacyPath, error);
        }
    }

    public setAutoValue(targetPath: AbsoluteModelPath, value: string | undefined): void {
        this.model.setViewModelField(this.resolveIterationContextsStrict(targetPath), "autoValue", value);
    }

    public get(modelPath: AbsoluteModelPath): string | undefined {
        return this.model.getValue(this.resolveIterationContextsStrict(modelPath));
    }

    public getInstances(modelPath: AbsoluteModelPath): Iterable<string> {
        return this.model.getChildren(this.resolveIterationContextsStrict(modelPath)) ?? [];
    }

    public getInstanceCount(modelPath: AbsoluteModelPath): number {
        return (this.model.getChildren(this.resolveIterationContextsStrict(modelPath)) ?? []).length;
    }

    public getAutoValue(modelPath: AbsoluteModelPath): string | undefined {
        return this.model.getViewModelField(this.resolveIterationContextsStrict(modelPath), "autoValue") as
            | string
            | undefined;
    }

    public getIgnoreAutoFlag(modelPath: AbsoluteModelPath): boolean {
        return (
            (this.model.getViewModelField(
                this.resolveIterationContextsStrict(modelPath),
                "ignoreAutoFlag"
            ) as boolean) ?? false
        );
    }

    public *getAllResolvedValues(modelPath: AbsoluteModelPath): Iterable<undefined | string> {
        const resolvedPath = this.resolveIterationContexts(modelPath).toAbsolute();
        const allResolvedPaths = MaskResolver.resolvePath(this.model, resolvedPath);
        for (const path of allResolvedPaths) {
            yield this.model.getValue(path);
        }
    }

    public getAutoFlag(modelPath: AbsoluteModelPath): boolean | undefined {
        const sourceValue = this.model.getViewModelField(this.resolveIterationContextsStrict(modelPath), "autoFlag");
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return sourceValue === "true" ? true : sourceValue;
    }

    public pushIterationContext(iterationContext: IterationContext): IEvaluationContext {
        return new EvaluationContext(
            this.model,
            this.errorManager,
            this.relatedEvents,
            this.ignoredPathChecker,
            this.picklistsProvider,
            this.externalPropertiesProvider,
            [...this.iterationContexts, iterationContext]
        );
    }

    public popIterationContext(): IEvaluationContext {
        return new EvaluationContext(
            this.model,
            this.errorManager,
            this.relatedEvents,
            this.ignoredPathChecker,
            this.picklistsProvider,
            this.externalPropertiesProvider,
            this.iterationContexts.slice(0, this.iterationContexts.length - 1)
        );
    }

    public isIgnoredPath(targetPath: AbsoluteModelPath): boolean {
        return this.ignoredPathChecker.isIgnoredPath(this.resolveIterationContextsStrict(targetPath));
    }

    private resolveIterationContexts(targetPath: AbsoluteModelPath): AbsoluteModelPath {
        let result = targetPath;
        for (const iterationContext of this.iterationContexts) {
            result = iterationContext.applyTo(result);
        }
        return result;
    }

    private resolveIterationContextsStrict(targetPath: AbsoluteModelPath): AbsoluteResolvedModelPath {
        let result = targetPath;
        for (const iterationContext of this.iterationContexts) {
            result = iterationContext.applyTo(result);
        }
        if (!result.isResolved()) {
            throw new InvalidArgumentError("targetPath", `Target path must be fully resolved`);
        }
        return result;
    }
}
